<template>
  <div class="search-page">
    <header class="search-header">
      <div class="search-bar">
        <button class="back-button" type="button" :aria-label="$t('common.back')" @click="goBack">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m15 5-7 7 7 7" />
          </svg>
        </button>

        <div class="search-field">
          <svg class="search-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="6.5" />
            <path d="m16 16 4 4" />
          </svg>
          <input
            ref="searchInput"
            v-model="searchKeyword"
            class="search-input"
            :placeholder="$t('search.placeInputHolder')"
            @keyup.enter="onSearch"
            @input="onInput"
          />
          <button
            v-if="searchKeyword"
            class="clear-button"
            type="button"
            aria-label="Clear"
            @click="clearSearch"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="m9 9 6 6m0-6-6 6" />
            </svg>
          </button>
        </div>

        <button class="search-button" type="button" @click="onSearch">
          {{ $t('search.searchBtn') }}
        </button>
      </div>
    </header>

    <main class="main-content">
      <template v-if="!showResult">
        <section class="search-section">
          <h2>{{ $t('search.popularSearch') }}</h2>
          <div class="popular-tags">
            <button
              v-for="tag in popularTags"
              :key="tag"
              class="tag"
              type="button"
              @click="searchByTag(tag)"
            >
              {{ $t(`search.tags.${tag}`) }}
            </button>
          </div>
        </section>

        <section class="search-section">
          <h2>{{ $t('search.recentSearch') }}</h2>
          <div class="history-list">
            <div v-if="!searchHistory.length" class="history-empty">
              {{ $t('search.noHistory') }}
            </div>

            <button
              v-for="(item, index) in searchHistory"
              v-else
              :key="`${item}-${index}`"
              class="history-item"
              type="button"
              @click="searchByHistory(item)"
            >
              <svg class="history-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4.5 8A8.5 8.5 0 1 1 3.8 15" />
                <path d="M4.5 3v5h5M12 7.5V12l3 2" />
              </svg>
              <span>{{ item }}</span>
              <span class="delete-button" role="button" @click.stop="deleteHistory(index)">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m8 8 8 8m0-8-8 8" />
                </svg>
              </span>
            </button>
          </div>
        </section>
      </template>

      <section v-else class="result-section">
        <div class="result-header">
          <div>
            <span>{{ $t('search.searchResultOf') }}</span>
            <h2>{{ searchKeyword }}</h2>
          </div>
          <strong>{{ searchResults.length }} {{ $t('search.countUnit') }}</strong>
        </div>

        <div v-if="loading" class="loading-grid">
          <div v-for="item in 6" :key="item" class="loading-card"></div>
        </div>

        <div v-else-if="searchResults.length" class="result-grid">
          <article
            v-for="item in searchResults"
            :key="item.id"
            class="result-card"
            @click="goToDetail(item)"
          >
            <div class="result-cover">
              <img :src="item.img" :alt="item.name" loading="lazy" />
              <span>{{ item.sum }}</span>
            </div>
            <h3>{{ item.name }}</h3>
          </article>
        </div>

        <div v-else class="empty-state">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="6.5" />
            <path d="m16 16 4 4M8.5 11h5" />
          </svg>
          <strong>{{ $t('search.noWorks') }}</strong>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { videoSearch } from '@/apis/search';
import { Toast } from 'vant';

export default {
  name: 'SearchIndex',
  data() {
    return {
      searchKeyword: '',
      showResult: false,
      loading: false,
      popularTags: [
        'romance',
        'suspense',
        'comedy',
        'action',
        'fantasy',
        'historical',
        'youth',
        'revenge',
        'sweet',
        'mystery',
        'crime',
        'completed',
        'new'
      ],
      searchHistory: [],
      searchResults: []
    };
  },
  mounted() {
    this.loadSearchHistory();

    if (this.$route.query.keyword) {
      this.searchKeyword = this.$route.query.keyword;
      this.onSearch();
    }

    if (this.$route.query.autoFocus === 'true') {
      this.$nextTick(() => this.$refs.searchInput?.focus());
    }
  },
  methods: {
    loadSearchHistory() {
      try {
        const history = localStorage.getItem('searchHistory');
        this.searchHistory = history ? JSON.parse(history) : [];
      } catch (error) {
        console.error('加载搜索历史失败:', error);
        this.searchHistory = [];
      }
    },
    saveSearchHistory() {
      const keyword = this.searchKeyword.trim();
      if (!keyword) return;

      const history = this.searchHistory.filter(item => item !== keyword);
      history.unshift(keyword);
      this.searchHistory = history.slice(0, 10);
      localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory));
    },
    onInput(event) {
      this.searchKeyword = event.target.value;
    },
    clearSearch() {
      this.searchKeyword = '';
      this.showResult = false;
      this.searchResults = [];
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    onSearch() {
      const keyword = this.searchKeyword.trim();
      if (!keyword) {
        Toast(this.$t('search.placeholder'));
        return;
      }

      this.saveSearchHistory();
      this.performSearch(keyword);
    },
    async performSearch(keyword) {
      this.showResult = true;
      this.loading = true;

      try {
        const data = await videoSearch({ keytext: keyword });
        this.searchResults = data && Array.isArray(data.rows) ? data.rows : [];
      } catch (error) {
        console.error('搜索失败:', error);
        this.searchResults = [];
      } finally {
        this.loading = false;
      }
    },
    searchByTag(tag) {
      this.searchKeyword = this.$t(`search.tags.${tag}`);
      this.onSearch();
    },
    searchByHistory(item) {
      this.searchKeyword = item;
      this.onSearch();
    },
    deleteHistory(index) {
      this.searchHistory.splice(index, 1);
      localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory));
    },
    goBack() {
      this.$router.back();
    },
    goToDetail(item) {
      this.$router.push(`/video/details/details?vid=${item.id}`);
    }
  }
};
</script>

<style scoped>
.search-page {
  width: 100%;
  max-width: 450px;
  min-height: 100vh;
  margin: 0 auto;
  background: #f5f5f7;
  color: #1d1d1f;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
}

.search-header {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(245, 245, 247, 0.9);
  backdrop-filter: saturate(180%) blur(20px);
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #007aff;
}

.back-button svg {
  width: 25px;
  height: 25px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.search-field {
  position: relative;
  flex: 1;
  min-width: 0;
}

.search-input {
  width: 100%;
  height: 38px;
  padding: 0 34px;
  border: 0;
  border-radius: 11px;
  outline: 0;
  background: #e8e8ed;
  color: #1d1d1f;
  font: inherit;
  font-size: 14px;
  box-sizing: border-box;
}

.search-input::placeholder {
  color: #8e8e93;
}

.search-icon {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 18px;
  height: 18px;
  fill: none;
  stroke: #8e8e93;
  stroke-width: 2;
  stroke-linecap: round;
  pointer-events: none;
}

.clear-button {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #8e8e93;
}

.clear-button svg {
  width: 18px;
  height: 18px;
  fill: #8e8e93;
  stroke: #e8e8ed;
  stroke-width: 1.8;
  stroke-linecap: round;
}

.search-button {
  flex: 0 0 auto;
  padding: 7px 2px;
  border: 0;
  background: transparent;
  color: #007aff;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
}

.main-content {
  padding: 24px 16px 40px;
}

.search-section + .search-section {
  margin-top: 30px;
}

.search-section h2 {
  margin: 0 0 13px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.035em;
}

.popular-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
}

.tag {
  min-height: 34px;
  padding: 0 14px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: #1d1d1f;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.04);
}

.tag:active {
  background: #e8e8ed;
  transform: scale(0.97);
}

.history-list {
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.history-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 54px;
  padding: 0 14px;
  gap: 12px;
  border: 0;
  border-bottom: 1px solid #e5e5ea;
  background: transparent;
  color: #1d1d1f;
  font: inherit;
  font-size: 13px;
  text-align: left;
}

.history-item:last-child {
  border-bottom: 0;
}

.history-item:active {
  background: #f2f2f7;
}

.history-item > span:nth-child(2) {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-icon {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  fill: none;
  stroke: #8e8e93;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.delete-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #c7c7cc;
}

.delete-button svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.history-empty {
  padding: 34px 16px;
  color: #8e8e93;
  font-size: 12px;
  text-align: center;
}

.result-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 18px;
  gap: 12px;
}

.result-header span {
  color: #8e8e93;
  font-size: 11px;
}

.result-header h2 {
  max-width: 280px;
  margin: 3px 0 0;
  overflow: hidden;
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.04em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-header strong {
  flex: 0 0 auto;
  color: #8e8e93;
  font-size: 11px;
  font-weight: 500;
}

.result-grid,
.loading-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 12px;
}

.result-card:active {
  transform: scale(0.98);
}

.result-cover {
  position: relative;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  border-radius: 16px;
  background: #e5e5ea;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.07);
}

.result-cover img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.result-cover span {
  position: absolute;
  right: 7px;
  bottom: 7px;
  min-width: 22px;
  padding: 4px 7px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 9px;
  text-align: center;
  backdrop-filter: blur(8px);
}

.result-card h3 {
  display: -webkit-box;
  min-height: 37px;
  margin: 9px 2px 0;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.01em;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.loading-card {
  aspect-ratio: 4 / 5.5;
  border-radius: 16px;
  background: linear-gradient(100deg, #e5e5ea 30%, #f2f2f7 50%, #e5e5ea 70%);
  background-size: 300% 100%;
  animation: loading-shimmer 1.4s infinite;
}

.empty-state {
  display: flex;
  align-items: center;
  padding: 70px 0;
  flex-direction: column;
  color: #8e8e93;
  text-align: center;
}

.empty-state svg {
  width: 42px;
  height: 42px;
  margin-bottom: 12px;
  fill: none;
  stroke: #c7c7cc;
  stroke-width: 1.5;
  stroke-linecap: round;
}

.empty-state strong {
  font-size: 13px;
  font-weight: 500;
}

@keyframes loading-shimmer {
  to {
    background-position: -150% 0;
  }
}
</style>
